/* eslint-disable max-lines */
import { type FeatureFlagRequirement, performanceFeatureFlags } from '@redviking/argonaut-util/src/feature-flags';
import type { ArgoRoleAclInsertInput, ValidationResult } from 'types';
import { AddRoleDocument, RoleFragment, UpdateRoleDocument } from 'types/db';
import { validateRole } from './roles.validations';
import { gqlClient } from 'src/util/gql-client';
import type { EntityModifyParameters, ExtendedEntityParameters } from 'src/components/EntityDetail';
import { v4 as uuidv4 } from 'uuid';

export type RoleEntity = Pick<RoleFragment, 'name' | 'enabled' | 'id'> & {
    /** IMPORTANT: If the context id is null the role is auto generated out of the box and should be the indicator that the role shouldnt be edited */
    contextId: string | null;
    acls: {
        [ acl_id: string ]: boolean;
    };
};

export type Permission = {
    id: string; // this should match the acl id
    name: string;
    description?: string;
    permDependencies?: string[];
    featureFlagRequirements?: FeatureFlagRequirement[];
};

export type PermissionGroup = {
    id: string;
    name: string;
    description?: string;
    icon?: string;
    featureFlagRequirements: FeatureFlagRequirement[];
    permissions: Permission[];
}

export const permissionGroupList: PermissionGroup[] = [
    {
        id: 'connection',
        name: 'Connections',
        icon: 'mdi-connection',
        featureFlagRequirements: [ 'unstableConnections' ],
        permissions: [
            {
                id: 'connection_select',
                name: 'View Connections',
                description: 'Allows viewing of connections.',
            },
            {
                id: 'connection_update',
                name: 'Manage Connections',
                permDependencies: [ 'connection_select' ],
                description: 'Allows modification of connections.',
            },
        ],
    },
    {
        id: 'location',
        name: 'Locations',
        icon: '$argo-location',
        featureFlagRequirements: [],
        permissions: [
            {
                id: 'location_select',
                name: 'View Locations',
                description: 'Allows viewing of locations, their attributes and schedules.',
            },
            {
                id: 'location_update',
                name: 'Manage Locations',
                permDependencies: [ 'location_select' ],
                description: 'Allows modification of locations, their attributes and schedules.',
            },
        ],
    },
    {
        id: 'attribute_schema',
        name: 'Attributes',
        icon: '$argo-attribute',
        featureFlagRequirements: [ 'attributes' ],
        permissions: [
            {
                id: 'attribute_select',
                name: 'View Attribute Data',
                description: 'Allows viewing of attribute schemas and their attributes.',
            },
            {
                id: 'attribute_update',
                name: 'Manage Attribute Data',
                permDependencies: [ 'attribute_select' ],
                description: 'Allows modification of attribute schemas and their attributes.',
            },
        ],
    },
    {
        id: 'material',
        name: 'Materials',
        icon: '$argo-material',
        featureFlagRequirements: [],
        permissions: [
            {
                id: 'material_select',
                name: 'View Materials and Material Configurations',
                description: 'Allows viewing of materials, material classes, material models and their attributes.',
            },
            {
                id: 'material_update',
                permDependencies: [ 'material_select' ],
                name: 'Manage Materials and Material Configurations',
                description: 'Allows modification of materials, material classes, material models and their attributes.',
            },
        ],
    },
    {
        id: 'serial_number',
        name: 'Serial Numbers',
        icon: '$argo-serial-number',
        featureFlagRequirements: [ 'serialNumbers' ],
        permissions: [
            {
                id: 'sn_format_select',
                name: 'View Serial Number Formats',
            },
            {
                id: 'sn_format_update',
                name: 'Manage Serial Number Formats',
                permDependencies: [ 'sn_format_select' ],
            },
        ],
    },
    {
        id: 'process',
        name: 'Processes',
        icon: '$argo-process',
        featureFlagRequirements: [ 'processes' ],
        permissions: [
            {
                id: 'process_select',
                name: 'View Processes',
                description: 'Allows viewing of processes and their versions.',
            },
            {
                id: 'process_update',
                name: 'Manage Processes',
                permDependencies: [ 'process_select' ],
                description: 'Allows modification of processes and their versions.',
            },
            {
                id: 'process_approve',
                name: 'Approve Process Versions',
                description: 'Allows approving of processes versions.',
                permDependencies: [ 'process_select' ],
            },
            {
                id: 'process_report_select',
                name: 'View Process Reports',
                permDependencies: [ 'process_select' ],
            },
        ],
    },
    {
        id: 'applet',
        name: 'Applets',
        icon: '$argo-design',
        featureFlagRequirements: [ 'applets' ],
        permissions: [
            {
                id: 'applet_select',
                name: 'View Applet Designs and Deployments',
                description: 'Allows viewing of applet designs and deployments.',
            },
            {
                id: 'applet_manager',
                permDependencies: [ 'applet_select' ],
                name: 'Manage Applet Designs and Deployments',
                description: 'Allows modification of applet designs, applet runners and deployments. (as well as the Applet Inspector)',
            },
        ],
    },
    {
        id: 'calendar',
        name: 'Calendars',
        icon: '$argo-calendar',
        featureFlagRequirements: [ 'schedules' ],
        permissions: [
            {
                id: 'calendar_select',
                name: 'View Calendars',
                description: 'Allows viewing of calendars, schedules and events.',
            },
            {
                id: 'calendar_update',
                name: 'Manage Calendars',
                permDependencies: [ 'calendar_select' ],
                description: 'Allows modification of calendars, schedules, events and event types.',
            },
        ],
    },
    {
        id: 'work_order',
        name: 'Work Orders',
        icon: '$argo-work-order',
        featureFlagRequirements: [ 'unstableWorkOrders' ],
        permissions: [
            {
                id: 'work_order_select',
                name: 'View Work Orders',
            },
            {
                id: 'work_order_update',
                name: 'Manage Work Orders',
                permDependencies: [ 'work_order_select' ],
            },
            {
                id: 'work_order_status_update',
                name: 'Manage Work Order Statuses',
                description: 'Allows manual modification of work order statuses.',
            },
        ],
    },
    {
        id: 'work_instruction',
        name: 'Work Instructions',
        featureFlagRequirements: [ 'unstableWorkInstructions' ],
        permissions: [
            {
                id: 'work_instruction_select',
                name: 'View Work Instructions',
            },
            {
                id: 'work_instruction_update',
                name: 'Manage Work Instructions',
                permDependencies: [ 'work_instruction_select' ],
            },
        ],
    },
    {
        id: 'role',
        name: 'Users and Permissions',
        icon: '$argo-role',
        featureFlagRequirements: [],
        permissions: [
            {
                id: 'role_select',
                name: 'View Users and Permissions',
                description: 'Allows viewing of users, roles and their permissions.',
            },
            {
                id: 'role_update',
                name: 'Manage Users and Permissions',
                permDependencies: [ 'role_select' ],
                description: 'Allows modification of users, their passwords, as well as roles and the permissions they include.',
            },
        ],
    },
    {
        id: 'misc',
        name: 'Miscellaneous',
        featureFlagRequirements: [],
        permissions: [
            // temporarily removing admin as it is too powerful
            // TODO: hid this behind a featuire flag
            // {
            //     id: 'admin',
            //     name: 'Administrative Privilages',
            // },
            {
                id: 'link_personal_update',
                name: 'Manage Personal Links',
                description: 'Allows management of personal links on the home page.',
            },
            {
                id: 'link_shared_update',
                name: 'Manage Shared Links',
                description: 'Allows management of shared links on the home page.',
            },
            {
                id: 'trpc_panel_visible',
                name: 'View tRPC Panel',
                featureFlagRequirements: [ 'api' ],
                description: 'Allows viewing of the tRPC panel for the Argonaut API.',
            },
            {
                id: 'gql_playground_visible',
                name: 'View GraphQL Playground',
                featureFlagRequirements: [ 'api' ],
                description: 'Allows viewing of the GraphQL Playground for the Hasura API.',
            },
        ],
    },
    {
        id: 'report',
        name: 'Performance Reports',
        featureFlagRequirements: [
            { or: performanceFeatureFlags }, // any of the performance feature flags
        ],
        permissions: [
            {
                id: 'report_select',
                name: 'View Performance Reports',
                description: 'Allows viewing of performance reports.',
            },
            {
                id: 'asset_update',
                name: 'Manage Performance Assets',
            },
        ],
    },
];

export const saveRole = async (payload: ExtendedEntityParameters<'role'>): Promise<ValidationResult> => {
    const {
        entity: role,
        oldEntity: originalRole,
    } = payload;

    const originalRoleAcls = originalRole?.acls || {};
    const deleteAclIds: string[] = [];
    const insertRoleAcls: ArgoRoleAclInsertInput[] = [];
    for (const [ acl, enabled ] of Object.entries(role.acls)) {
        if (enabled && !originalRoleAcls[acl]) {
            insertRoleAcls.push({
                id: uuidv4(),
                acl_id: acl,
                role_id: role.id,
            });
        }
        if (!enabled && originalRoleAcls[acl]) {
            deleteAclIds.push(acl);
        }
    }

    const validationResult = validateRole(role);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        await gqlClient.request({
            document: UpdateRoleDocument,
            variables: {
                roleId: role.id,
            role: {
                    name: role.name,
                    enabled: role.enabled,
                },
                insertRoleAcls,
                deleteRoleAclsBoolExp: {
                    acl_id: { _in: deleteAclIds },
                },
            },
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Role: "${JSON.stringify(err)}"`,
        };
    }
};

export const createRole = async (payload: EntityModifyParameters<'role'>): Promise<ValidationResult> => {
    const role = payload.entity;
    const validationResult = validateRole(role);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        await gqlClient.request({
            document: AddRoleDocument,
            variables: {
                role: {
                    id: role.id,
                    name: role.name,
                    enabled: role.enabled,
                    acls: {
                        data: Object.entries(role.acls).reduce<ArgoRoleAclInsertInput[]>((roleAcls, [ acl_id, enabled ]) => {
                            if (enabled) {
                                roleAcls.push({
                                    id: uuidv4(),
                                    acl_id,
                                });
                            }
                            return roleAcls;
                        }, []),
                    },
                },
            },
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Role: "${JSON.stringify(err)}"`,
        };
    }
};
